import React from 'react';
import Link from 'components/link';
import { type LinkProps } from 'components/link/link';
import styles from './subtle-link.module.scss';

export interface SubtleLinkProps extends LinkProps {
  children: React.ReactNode;
  /** Opens the link in a new browser tab with secure rel attributes applied automatically */
  openInNewTab?: boolean;
}

/**
 * SubtleLink - Link with no decoration that underlines on hover
 * 
 * Inherits color from parent and shows underline only on hover.
 * Use for links that should blend into surrounding text until interacted with.
 * 
 * @example
 * ```tsx
 * <SubtleLink href="/listing/123">
 *   <Address street="123 Main St" city="Toronto" />
 * </SubtleLink>
 * 
 * <SubtleLink href="/account/saved/properties" openInNewTab>
 *   My Favourites
 * </SubtleLink>
 * ```
 */
export function SubtleLink({
  children,
  className,
  openInNewTab,
  ...props
}: SubtleLinkProps) {
  const newTabProps = openInNewTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <Link
      {...props}
      {...newTabProps}
      className={className ? `${styles.subtleLink} ${className}` : styles.subtleLink}
    >
      {children}
    </Link>
  );
}

export default SubtleLink;
